import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useActiveUser } from "src/hooks/useActiveUser";
import { toast } from "src/lib/toast";
import api from "src/utils/api";

const NotificationContext = createContext({
  notifications: [],
  setNotifications: () => {},
  loading: false,
  handleNotificationClick: () => {},
  handleMarkAsRead: () => {},
  handleClearAllNotifications: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useActiveUser();
  const navigate = useNavigate();

  const getNotifications = useCallback(async () => {
    if (!user || user.userType !== "partner") {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get("partners/notifications");

      if (data) {
        setNotifications(
          data.data
            .map((item) => ({
              notificationId: item?.id,
              id: item?.attributes?.metadata?.partner_client?.id,
              clientName: item?.attributes?.metadata?.partner_client?.name,
              // Lead do chat do site (sac_gpt_ruby #212): a conversa do widget abre pela sessão.
              origem: item?.attributes?.metadata?.origem,
              widgetSessionId: item?.attributes?.metadata?.widget_session_id,
              // Aviso de agendamento (sac_gpt_ruby #213): link do evento no Google, quando existe.
              eventLink: item?.attributes?.metadata?.event_link,
              readed: item?.attributes?.readed,
              title: item?.attributes?.title,
            }))
            .sort((a, b) => b.id - a.id),
        );
      }
    } catch (error) {
      // Suppress 403 PHONE_REQUIRED - PhoneNumberModal handles it
      if (error?.response?.status === 403 && error?.response?.data?.code === "PHONE_REQUIRED")
        return;
      // Suppress 403 PLAN_REQUIRED - PrivateRoute redirects to /escolher-plano
      if (error?.response?.status === 403 && error?.response?.data?.code === "PLAN_REQUIRED")
        return;
      toast.error("Não foi possível carregar a lista de notificações.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.userType === "partner") {
      getNotifications();
    } else {
      setLoading(false);
    }
  }, [user?.userType, getNotifications]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      try {
        if (notification.title === "Novo Lead Recebido" && notification.origem === "widget") {
          if (notification.widgetSessionId) {
            navigate(`/conversas?client_id=${notification.widgetSessionId}&source=widget`);
          }
        } else if (notification.title === "Novo Lead Recebido" && notification.id) {
          navigate(`/conversas?client_id=${notification.id}&source=whatsapp`);
        } else if (/^https:\/\//.test(notification.eventLink || "")) {
          window.open(notification.eventLink, "_blank", "noopener,noreferrer");
        }

        // Mark as read when clicking
        await api.put(`partners/notifications/${notification.notificationId}`);

        setNotifications((prev) =>
          prev.map((item) =>
            item.notificationId === notification.notificationId ? { ...item, readed: true } : item,
          ),
        );
      } catch (error) {
        toast.error("Erro ao processar notificação.");
      }
    },
    [navigate],
  );

  const handleMarkAsRead = useCallback(async (notification) => {
    try {
      await api.put(`partners/notifications/${notification.notificationId}`);

      setNotifications((prev) =>
        prev.map((item) =>
          item.notificationId === notification.notificationId ? { ...item, readed: true } : item,
        ),
      );

      toast.success("Notificação marcada como lida.");
    } catch (error) {
      toast.error("Erro ao marcar notificação como lida.");
    }
  }, []);

  const handleClearAllNotifications = useCallback(async () => {
    try {
      // Only clear unread notifications
      const unreadNotifications = notifications.filter((n) => !n.readed);

      if (unreadNotifications.length === 0) {
        toast.info("Não há notificações não lidas para limpar.");
        return;
      }

      const requests = unreadNotifications.map((notification) =>
        api.put(`partners/notifications/${notification.notificationId}`),
      );

      await Promise.all(requests);

      // Mark all as read instead of removing them
      setNotifications((prev) => prev.map((item) => ({ ...item, readed: true })));

      toast.success("Todas as notificações foram marcadas como lidas.");
    } catch (error) {
      console.error("Erro ao limpar notificações:", error);
      toast.error("Erro ao limpar notificações.");
    }
  }, [notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        loading,
        handleNotificationClick,
        handleMarkAsRead,
        handleClearAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
